package fyi.moko.hotpl.data.image

import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/**
 * 사진 바이트를 담는 IndexedDB 오브젝트 스토어. [JsPlaceImageStore]만 쓴다.
 *
 * ── 왜 localStorage에서 옮겼는가 ──────────────────────────────
 * `localStorage`는 origin당 5~10MB **문자열** 저장소다. 사진을 base64로 넣으면 33%가 인코딩에
 * 그냥 날아가고, 1080px·q80 JPEG 한 장이 200~330KB라 **15~30장이면 한도에 닿는다.** 그리고
 * 닿았을 때 조용했다 — 예전 구현은 quota 예외를 console.error로 삼키고 파일명을 정상적으로
 * 돌려줬으므로, 장소는 사진 없이 저장되고 화면에는 아무 말도 뜨지 않았다.
 *
 * IndexedDB는 바이너리를 그대로 담고(base64 낭비 없음) 할당량이 보통 GB 단위다. 브라우저
 * 기본 API라 번들도 늘지 않는다.
 *
 * ── 왜 external 선언 대신 dynamic인가 ──────────────────────────────
 * 이 파일이 쓰는 IndexedDB 표면은 열기/put/get/delete/키 나열이 전부다. 그만큼만 `dynamic`으로
 * 다루는 편이, 스텁을 여럿 선언하고 stdlib 버전마다 있는지 확인하는 것보다 짧고 덜 깨진다.
 * 같은 저장소의 `JsCurrentLocationProvider`/`JsPlaceImageStore`도 같은 방식이다.
 */
internal object JsImageDb {

    private const val DB_NAME = "hotpl"
    private const val DB_VERSION = 1
    private const val STORE = "images"

    /** IndexedDB를 아예 못 쓰는 환경(사생활 보호 모드 등)인지. */
    val isAvailable: Boolean
        get() = runCatching { window.asDynamic().indexedDB != null }.getOrDefault(false)

    private var cached: dynamic = null

    private suspend fun db(): dynamic {
        val existing = cached
        if (existing != null) return existing
        val opened = suspendCancellableCoroutine { continuation ->
            val request = window.asDynamic().indexedDB.open(DB_NAME, DB_VERSION)
            request.onupgradeneeded = { event: dynamic ->
                val database = event.target.result
                if (!database.objectStoreNames.contains(STORE)) {
                    database.createObjectStore(STORE)
                }
            }
            request.onsuccess = { _: dynamic ->
                if (continuation.isActive) continuation.resume(request.result)
            }
            request.onerror = { _: dynamic ->
                if (continuation.isActive) {
                    continuation.resumeWithException(
                        IllegalStateException("IndexedDB open failed: ${request.error?.message}"),
                    )
                }
            }
        }
        cached = opened
        return opened
    }

    /**
     * 쓰기는 **트랜잭션 완료**를 기다린다. `request.onsuccess`는 요청이 큐에 들어갔다는 뜻일
     * 뿐이라, 거기서 끝내면 할당량 초과가 `transaction.onabort`로 뒤늦게 와서 놓친다 —
     * 이 구현이 없애려는 바로 그 조용한 실패다.
     */
    suspend fun put(key: String, bytes: ByteArray) {
        val database = db()
        suspendCancellableCoroutine { continuation ->
            val transaction = database.transaction(STORE, "readwrite")
            transaction.objectStore(STORE).put(bytes.toUint8Array(), key)
            transaction.oncomplete = { _: dynamic ->
                if (continuation.isActive) continuation.resume(Unit)
            }
            transaction.onabort = { _: dynamic ->
                if (continuation.isActive) {
                    continuation.resumeWithException(
                        ImageStoreFullException(transaction.error?.name?.toString()),
                    )
                }
            }
            transaction.onerror = { _: dynamic ->
                if (continuation.isActive) {
                    continuation.resumeWithException(
                        ImageStoreFullException(transaction.error?.name?.toString()),
                    )
                }
            }
        }
    }

    suspend fun get(key: String): ByteArray? {
        val database = db()
        return suspendCancellableCoroutine { continuation ->
            val request = database.transaction(STORE, "readonly").objectStore(STORE).get(key)
            request.onsuccess = { _: dynamic ->
                val result = request.result
                if (continuation.isActive) {
                    continuation.resume(if (result == null) null else uint8ArrayToByteArray(result))
                }
            }
            request.onerror = { _: dynamic ->
                if (continuation.isActive) continuation.resume(null)
            }
        }
    }

    suspend fun delete(key: String) {
        val database = db()
        suspendCancellableCoroutine { continuation ->
            val transaction = database.transaction(STORE, "readwrite")
            transaction.objectStore(STORE).delete(key)
            transaction.oncomplete = { _: dynamic ->
                if (continuation.isActive) continuation.resume(Unit)
            }
            transaction.onabort = { _: dynamic ->
                if (continuation.isActive) continuation.resume(Unit)
            }
        }
    }

    /** [prefix]로 시작하는 키 전부. 계정별 접두사로 지우기 위해 필요하다. */
    suspend fun keys(prefix: String): List<String> {
        val database = db()
        return suspendCancellableCoroutine { continuation ->
            val request = database.transaction(STORE, "readonly").objectStore(STORE).getAllKeys()
            request.onsuccess = { _: dynamic ->
                val all = request.result.unsafeCast<Array<String>>()
                if (continuation.isActive) continuation.resume(all.filter { it.startsWith(prefix) })
            }
            request.onerror = { _: dynamic ->
                if (continuation.isActive) continuation.resume(emptyList())
            }
        }
    }
}

/**
 * Kotlin/JS의 `ByteArray`가 `Int8Array`로 컴파일된다는 사실에 기대지 않고 바이트를 하나씩
 * 옮긴다 — 컴파일러 내부 표현이라 바뀔 수 있고, 구조적 복제에는 `Uint8Array`가 안전하다.
 * (`js(...)`는 상수 문자열만 받고 그 안의 `this`는 Kotlin 수신자가 아니므로 지역 변수로 넘긴다.)
 */
private fun ByteArray.toUint8Array(): dynamic {
    val length = size
    val out = js("new Uint8Array(length)")
    for (i in indices) out[i] = this[i].toInt() and 0xFF
    return out
}

/**
 * [toUint8Array]의 역방향. 부호 없는 0..255를 다시 부호 있는 바이트로 되돌린다.
 * (`dynamic` 확장 수신자는 컴파일러가 막으므로 인자로 받는다.)
 */
private fun uint8ArrayToByteArray(array: dynamic): ByteArray {
    val length = array.length.unsafeCast<Int>()
    return ByteArray(length) { index -> array[index].unsafeCast<Int>().toByte() }
}
